import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PropTypes } from 'prop-types';
import { useParams } from 'react-router';

import { customTunnelReducerCleared } from 'reducers/customTunnel';
import {
    useCloneInvestmentFundTunnelMutation,
    useGetInvestmentFundsQuery,
    useGetTunnelsQuery,
} from 'api/main';
import { fundTypes, stepIDs } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import TunnelCloning from './TunnelCloning';
import TunnelPreview from './TunnelPreview';
import TunnelStepActions from './actions/TunnelStepActions';
import { CancelButton, Loader } from 'components';
import { Box, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

const excludedStepIds = [
    stepIDs.USER_KNOWLEDGE,
    stepIDs.EXPERIENCE,
    stepIDs.USER_ADEQUACY,
    stepIDs.PREFERENCES,
];

const TunnelManagement = ({ onCancel }) => {
    const dispatch = useDispatch();
    const { fundId } = useParams();

    const { data: funds = [], isLoading: fundsLoader } = useGetInvestmentFundsQuery();
    const [, { isLoading: cloneLoading }] = useCloneInvestmentFundTunnelMutation({
        fixedCacheKey: 'tunnel-cloning',
    });

    const [value, setValue] = useState('1');
    const { data: pendingTunnels = [], isLoading: currentLoader } = useGetTunnelsQuery({
        investmentFunds: fundId,
        status: 'pending',
    });

    const handleTab = useCallback(
        (_e, newValue) => {
            dispatch(customTunnelReducerCleared());
            setValue(newValue);
        },
        [dispatch]
    );

    const currentFund = useMemo(() => funds.find(f => f.id === +fundId), [fundId, funds]);

    const isFCPR = useMemo(() => currentFund?.type === fundTypes.FCPR, [currentFund?.type]);

    const pendingPPTunnel = useMemo(
        () => pendingTunnels?.find(t => t.name.includes('pp')),
        [pendingTunnels]
    );
    const pendingPMTunnel = useMemo(
        () => pendingTunnels?.find(t => t.name.includes('pm')),
        [pendingTunnels]
    );

    const ppPendingTunnelSteps = useMemo(
        () =>
            pendingPPTunnel
                ? pendingPPTunnel.tunnelPhases
                      .reduce((acc, ph) => {
                          acc = acc.concat(
                              ph.tunnelSteps.map(ts => ({ ...ts, tunnelPhaseId: ph.id }))
                          );
                          return acc;
                      }, [])
                      .filter(ts => !excludedStepIds.includes(ts.step.id))
                : [],
        [pendingPPTunnel]
    );

    const pmPendingTunnelSteps = useMemo(
        () =>
            pendingPMTunnel
                ? pendingPMTunnel.tunnelPhases
                      .reduce((acc, ph) => {
                          acc = acc.concat(
                              ph.tunnelSteps.map(ts => ({ ...ts, tunnelPhaseId: ph.id }))
                          );
                          return acc;
                      }, [])
                      .filter(ts => !excludedStepIds.includes(ts.step.id))
                : [],
        [pendingPMTunnel]
    );

    return (
        <Box width="100%" padding={1}>
            <Loader loading={fundsLoader}>
                <TabContext value={value}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={handleTab} aria-label="lab API tabs example">
                            <Tab disableRipple label={texts.TUNNELS} value="1" />
                            <Tab disableRipple label={texts.TUNNEL_STEPS} value="2" />
                            <Tab disableRipple label={texts.ACTIONS} value="3" />
                        </TabList>
                    </Box>
                    <TabPanel value="1">
                        <TunnelCloning
                            fundId={+fundId}
                            fundType={currentFund?.type}
                            pendingTunnels={pendingTunnels}
                            excludedStepIds={excludedStepIds}
                            ppTunnelSteps={ppPendingTunnelSteps}
                            pmTunnelSteps={pmPendingTunnelSteps}
                        />
                    </TabPanel>
                    <TabPanel value="2">
                        {!!(ppPendingTunnelSteps?.length && pmPendingTunnelSteps?.length) && (
                            <TunnelPreview
                                loading={currentLoader}
                                isFCPR={isFCPR}
                                pendingPPTunnel={pendingPPTunnel}
                                pendingPMTunnel={pendingPMTunnel}
                                ppTunnelSteps={ppPendingTunnelSteps}
                                pmTunnelSteps={pmPendingTunnelSteps}
                            />
                        )}
                    </TabPanel>
                    <TabPanel value="3">
                        {!!(ppPendingTunnelSteps?.length && pmPendingTunnelSteps?.length) && (
                            <TunnelStepActions
                                ppTunnelId={pendingPPTunnel?.id}
                                pmTunnelId={pendingPMTunnel?.id}
                            />
                        )}
                    </TabPanel>
                </TabContext>
                <Box textAlign="center">
                    <CancelButton disabled={cloneLoading} onClick={onCancel}>
                        {texts.RETOUR}
                    </CancelButton>
                </Box>
            </Loader>
        </Box>
    );
};

TunnelManagement.propTypes = { onCancel: PropTypes.func };

export default TunnelManagement;
